import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Button,
  Typography,
  Chip,
  IconButton,
  Avatar,
  Paper,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Share as ShareIcon,
  Image as ImageIcon,
  MoreVert as MoreVertIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { format, differenceInDays, addDays, isAfter, isBefore, isWithinInterval } from 'date-fns';

const TravelPlanCard = ({
  plan,
  isOwner = false,
  expandedAccordions = {},
  onAccordionChange,
  onDelete,
  onEdit,
  onCopy,
  onLike,
  onUnlike,
  isExploreTab = false,
  isLiked = false,
  activeItemIndex = 0,
  onItemSelected,
  onEditCoverImage,
  onPrivacyUpdate,
  onCardClick
}) => {
  const navigate = useNavigate();
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowActionsDropdown(false);
      }
    };

    // Add event listener when dropdown is open
    if (showActionsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    // Cleanup event listeners
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showActionsDropdown]);
  
  // Calculate trip status based on dates
  const getTripStatus = () => {
    if (!plan.startDate || !plan.endDate) return { status: 'Upcoming', color: '#3498db' };
    
    const now = new Date();
    const startDate = new Date(plan.startDate);
    const endDate = new Date(plan.endDate);
    
    if (isBefore(now, startDate)) {
      return { status: 'Upcoming', color: '#3498db' };
    } else if (isWithinInterval(now, { start: startDate, end: endDate })) {
      return { status: 'Currently Traveling', color: '#27ae60' };
    } else {
      return { status: 'Completed', color: '#95a5a6' };
    }
  };

  // Calculate progress
  const getProgress = () => {
    if (!plan.startDate || !plan.endDate) return { percentage: 0, display: '0%' };
    
    const now = new Date();
    const startDate = new Date(plan.startDate);
    const endDate = new Date(plan.endDate);
    const status = getTripStatus().status;
    
    if (status === 'Upcoming') {
      const daysUntilTrip = differenceInDays(startDate, now);
      return { percentage: 0, display: `${daysUntilTrip} days to go` };
    } else if (status === 'Currently Traveling') {
      const totalDays = differenceInDays(endDate, startDate) + 1;
      const daysPassed = differenceInDays(now, startDate) + 1;
      const percentage = Math.round((daysPassed / totalDays) * 100);
      return { percentage, display: `${percentage}%` };
    } else {
      return { percentage: 100, display: '100%' };
    }
  };

  // Calculate day count
  const dayCount = plan.startDate && plan.endDate
    ? differenceInDays(new Date(plan.endDate), new Date(plan.startDate)) + 1
    : plan.dayCount || 1;

  // Group events by day function
  const groupEventsByDay = (events, startDate) => {
    const planStartDate = startDate ? new Date(startDate) : null;
    if (planStartDate) {
      planStartDate.setHours(0, 0, 0, 0);
    }
    
    const groupedEvents = {};
    
    events.forEach(event => {
      let dayNumber;
      
      if (event.day) {
        dayNumber = event.day;
      } else if (planStartDate && event.start) {
        const eventDate = new Date(event.start);
        eventDate.setHours(0, 0, 0, 0);
        const dayDifference = differenceInDays(eventDate, planStartDate);
        dayNumber = dayDifference + 1;
      } else {
        dayNumber = 1;
      }
      
      if (!groupedEvents[dayNumber]) {
        groupedEvents[dayNumber] = [];
      }
      
      groupedEvents[dayNumber].push(event);
    });
    
    Object.keys(groupedEvents).forEach(day => {
      groupedEvents[day].sort((a, b) => {
        if (a.start && b.start) {
          return new Date(a.start) - new Date(b.start);
        }
        if (a.start) return -1;
        if (b.start) return 1;
        return 0;
      });
    });
    
    return groupedEvents;
  };


  const tripStatus = getTripStatus();
  const progress = getProgress();

  // Handle actions dropdown
  const handleActionsToggle = () => {
    setShowActionsDropdown(!showActionsDropdown);
  };

  const handleShare = () => {
    setShowActionsDropdown(false);
    setShareDialogOpen(true);
  };

  const handleShareConfirm = async () => {
    try {
      if (onPrivacyUpdate) {
        await onPrivacyUpdate(plan.id || plan._id, { privacy: 'public' });
      }
      setShareDialogOpen(false);
    } catch (error) {
      console.error('Error updating privacy:', error);
      // Keep dialog open if there's an error
    }
  };


  const handleEditCoverImage = () => {
    setShowActionsDropdown(false);
    if (onEditCoverImage) {
      onEditCoverImage(plan.id || plan._id);
    }
  };

  const handleDelete = () => {
    setShowActionsDropdown(false);
    onDelete(plan.id || plan._id);
  };

  // Get cover image URL
  const getCoverImageUrl = () => {
    if (plan.coverImageUrl) {
      return plan.coverImageUrl;
    }
    // Default cover images
    const defaultCovers = [
      '/covers/adventure.png',
      '/covers/beach.png', 
      '/covers/city.png',
      '/covers/food_shopping.png',
      '/covers/history.png',
      '/covers/nature.png'
    ];
    // Use plan ID to consistently pick the same default cover
    const index = plan.id ? plan.id.length % defaultCovers.length : 0;
    return defaultCovers[index];
  };

  return (
    <>
      <Card 
        onClick={() => onCardClick && onCardClick(plan)}
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          transition: 'all 0.2s ease-in-out',
          cursor: onCardClick ? 'pointer' : 'default',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
          }
        }}
      >
        {/* Cover Image Header */}
        <CardMedia
          component="div"
          sx={{
            height: 160,
            backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.6)), url(${getCoverImageUrl()})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            p: 2,
            color: 'white'
          }}
        >
          {/* Top Section with Status and Actions */}
          <Box sx={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}>
            <Chip
              label={tripStatus.status}
              sx={{
                backgroundColor: tripStatus.color,
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.75rem'
              }}
            />
            
            {isOwner && (
              <Box sx={{ position: 'relative' }} ref={dropdownRef}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleActionsToggle();
                  }}
                  sx={{ 
                    color: 'white',
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.5)'
                    }
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
                
                <Collapse in={showActionsDropdown}>
                  <Paper
                    elevation={8}
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      mt: 1,
                      minWidth: 160,
                      zIndex: 1000,
                      borderRadius: 2
                    }}
                  >
                    <Box sx={{ py: 1 }}>
                      <Button
                        fullWidth
                        startIcon={<ImageIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditCoverImage();
                        }}
                        sx={{ 
                          justifyContent: 'flex-start',
                          px: 2,
                          py: 1,
                          color: 'text.primary',
                          textTransform: 'none'
                        }}
                      >
                        Edit Cover
                      </Button>
                      <Button
                        fullWidth
                        startIcon={<ShareIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShare();
                        }}
                        sx={{ 
                          justifyContent: 'flex-start',
                          px: 2,
                          py: 1,
                          color: 'text.primary',
                          textTransform: 'none'
                        }}
                      >
                        Share Trip
                      </Button>
                      <Button
                        fullWidth
                        startIcon={<DeleteIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete();
                        }}
                        sx={{ 
                          justifyContent: 'flex-start',
                          px: 2,
                          py: 1,
                          color: 'error.main',
                          textTransform: 'none'
                        }}
                      >
                        Delete Trip
                      </Button>
                    </Box>
                  </Paper>
                </Collapse>
              </Box>
            )}
          </Box>

          {/* Bottom Section with Destination */}
          <Box>
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>
              {plan.destination || 'Unknown Destination'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {plan.startDate && plan.endDate 
                ? `${format(new Date(plan.startDate), 'MMM d')} - ${format(new Date(plan.endDate), 'MMM d, yyyy')}`
                : 'Dates not set'
              }
            </Typography>
          </Box>
        </CardMedia>

        {/* Card Content */}
        <Box sx={{ flex: 1, p: 2 }}>
          {/* Destinations and Budget Row */}
          <Box sx={{ 
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2
          }}>
            {/* Destinations Section */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Destinations
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 0.5,
                mt: 1
              }}>
                {plan.destinations && plan.destinations.length > 0 ? (
                  plan.destinations.map((destination, index) => (
                    <Chip
                      key={index}
                      label={destination.shortName || destination.name || destination}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(255, 123, 107, 0.1)',
                        color: '#FF7B6B',
                        borderColor: '#FF7B6B',
                        fontSize: '0.75rem',
                        height: '24px',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 123, 107, 0.2)'
                        }
                      }}
                      variant="outlined"
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {plan.destination || 'No destinations set'}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Budget Section */}
            <Box sx={{ textAlign: 'right', minWidth: 100 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Budget
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight="bold" color="primary.main">
                  ${plan.currentSpent || 0}/${plan.totalBudget || 0}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Progress Section */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1
            }}>
              <Typography variant="body2" color="text.secondary">
                Trip Progress
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {progress.display}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress.percentage}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: tripStatus.color,
                  borderRadius: 4
                }
              }}
            />
          </Box>
        </Box>
      </Card>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)}>
        <DialogTitle>Share Trip</DialogTitle>
        <DialogContent>
          <Typography>
            Do you want to make this travel plan public so others can view and copy it?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Once shared, your travel plan will be visible in the Explore section for other users to discover and copy.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleShareConfirm} variant="contained">
            Make Public
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TravelPlanCard; 